import { Injectable, signal, computed, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, firstValueFrom, of, throwError } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { CurrentUser } from '../common/nav-data';
import { environment } from '../../environments/environment';
import { NotificationsService } from './notifications.service';

/** Backend response envelope (see backend ResponseInterceptor). */
interface ApiEnvelope<T> {
  success: boolean;
  statusCode: number;
  message: string;
  data: T;
}

/** `data` payload of POST /v1/auth/login. */
interface LoginData {
  accessToken: string;
  tokenType: string;
  expiresIn: string;
  user: { id: string; name: string; email: string; role: string };
}

/** Read vs Write access level for a (user, module) grant. */
export type PermissionLevel = 'R' | 'W';

/** A single (module, level) grant resolved for the current user. */
export interface ResolvedPermission {
  moduleId: string;
  moduleName: string;
  permission: PermissionLevel;
}

/** `data` payload of GET /v1/auth/me (the authenticated principal). */
interface MeData {
  id: string;
  name: string;
  email: string;
  roleId: string;
  roleName: string;
  permissions?: ResolvedPermission[];
  mustChangePassword: boolean;
}

const API_BASE = environment.apiBaseUrl;
const STORAGE_KEY = 'normax_crm_user';

/** Role whose users bypass all per-module permission checks (mirrors the backend). */
const SUPER_ROLE = 'ADMIN';

// The backend user has no "entity" concept yet — supply display defaults so the
// topbar chip renders. Replace once the API exposes entity data.
const DEFAULT_ENTITY = 'NORMAX CAPITAL K.B.';
const DEFAULT_ENTITY_SHORT = 'NORMAX';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly http = inject(HttpClient);
  private readonly notifications = inject(NotificationsService);
  private readonly userSignal = signal<CurrentUser | null>(null);
  private readonly userIdSignal = signal<string | null>(null);
  private readonly emailSignal = signal<string | null>(null);
  private readonly roleNameSignal = signal<string | null>(null);
  private readonly permissionsSignal = signal<ResolvedPermission[]>([]);
  private readonly mustChangePasswordSignal = signal(false);

  readonly currentUser = computed(() => this.userSignal());
  readonly isLoggedIn = computed(() => this.userSignal() !== null);
  /** The authenticated user's id (available once the principal is loaded). */
  readonly userId = computed(() => this.userIdSignal());
  /** The authenticated user's email (available once the principal is loaded). */
  readonly email = computed(() => this.emailSignal());
  /** The authenticated user's role name (available once the principal is loaded). */
  readonly roleName = computed(() => this.roleNameSignal());
  /** The authenticated user's resolved per-module grants. */
  readonly permissions = computed(() => this.permissionsSignal());
  /** True while the user must change an auto-generated password before using the app. */
  readonly mustChangePassword = computed(() => this.mustChangePasswordSignal());

  constructor() {
    // Hydrate the display hint immediately; the httpOnly cookie is the source of
    // truth and is re-verified by restoreSession() on app start.
    this.loadSession();
  }

  /**
   * Authenticate against the backend. On success the server sets an httpOnly
   * JWT cookie (never exposed to JS); we then load the full principal (name,
   * role and per-module permissions) from /auth/me so authorization-gated UI is
   * accurate immediately, without waiting for a page refresh.
   *
   * Rejects (rather than resolving `false`) on failure so the caller sees the
   * real `HttpErrorResponse` — the backend's message is often specific and
   * actionable (e.g. deactivated account, no module permissions assigned),
   * not just "wrong credentials".
   */
  login(email: string, password: string): Observable<true> {
    return this.http
      .post<ApiEnvelope<LoginData>>(`${API_BASE}/auth/login`, {
        email,
        password,
      })
      .pipe(
        switchMap(() => this.fetchPrincipal()),
        map(() => true as const),
        catchError((err) => {
          this.clearUser();
          return throwError(() => err);
        }),
      );
  }

  /**
   * Verify the session cookie on app start by calling /auth/me. Hydrates the
   * user + permissions when the cookie is valid, clears them on 401. Returns a
   * promise so it can gate app bootstrap via provideAppInitializer.
   */
  restoreSession(): Promise<void> {
    return firstValueFrom(
      this.fetchPrincipal().pipe(
        catchError(() => {
          this.clearUser();
          return of(void 0);
        }),
      ),
    );
  }

  /** POST /auth/change-password — changes the authenticated user's own password. */
  changePassword(currentPassword: string, newPassword: string): Observable<{ message: string }> {
    return this.http
      .post<ApiEnvelope<{ message: string }>>(`${API_BASE}/auth/change-password`, {
        currentPassword,
        newPassword,
      })
      .pipe(map((res) => res.data));
  }

  /**
   * POST /auth/set-new-password — the forced first-password-set after a
   * temp-password login. No current password required: the session itself
   * (issued at login) already proves the temp password was known.
   */
  setNewPassword(newPassword: string): Observable<{ message: string }> {
    return this.http
      .post<ApiEnvelope<{ message: string }>>(`${API_BASE}/auth/set-new-password`, {
        newPassword,
      })
      .pipe(map((res) => res.data));
  }

  /** Clears the httpOnly cookie server-side and the local UI state. */
  logout(): void {
    this.http
      .post(`${API_BASE}/auth/logout`, {})
      .pipe(catchError(() => of(null)))
      .subscribe();
    this.clearUser();
    this.notifications.resetState();
  }

  /**
   * Clears local auth state after the server has already rejected the session
   * (a 401 from an authenticated call). Unlike `logout()` this makes no network
   * request — the cookie/token is already invalid — so it is safe to call from
   * an HTTP interceptor without risking a redirect/retry loop.
   */
  clearSession(): void {
    this.clearUser();
    this.notifications.resetState();
  }

  /**
   * True if the current user holds the required (module, level) grant. Mirrors
   * the backend PermissionsGuard: ADMIN bypasses all checks, and a `W` grant
   * satisfies an `R` requirement (write implies read).
   */
  hasPermission(module: string, level: PermissionLevel): boolean {
    if (!this.isLoggedIn()) {
      return false;
    }
    if (this.roleNameSignal() === SUPER_ROLE) {
      return true;
    }
    return this.permissionsSignal().some(
      (p) =>
        p.moduleName === module &&
        (p.permission === level ||
          (level === 'R' && p.permission === 'W')),
    );
  }

  /** GET /auth/me → hydrate the display user, id, role and permissions. */
  private fetchPrincipal(): Observable<void> {
    return this.http
      .get<ApiEnvelope<MeData>>(`${API_BASE}/auth/me`)
      .pipe(map((res) => this.setPrincipal(res.data)));
  }

  /** Apply a resolved principal to local state. */
  private setPrincipal(me: MeData): void {
    this.setUser(this.toCurrentUser(me.name, me.roleName));
    this.userIdSignal.set(me.id);
    this.emailSignal.set(me.email);
    this.roleNameSignal.set(me.roleName);
    this.permissionsSignal.set(me.permissions ?? []);
    this.mustChangePasswordSignal.set(me.mustChangePassword);
  }

  /**
   * Clears the forced-password-change flag immediately after a successful
   * `changePassword()` call, so route guards unblock without waiting on a
   * fresh `/auth/me` round trip.
   */
  clearMustChangePassword(): void {
    this.mustChangePasswordSignal.set(false);
  }

  /** Role-based landing route after login / once the forced change completes. */
  homeRoute(): string {
    return this.roleNameSignal() === SUPER_ROLE ? '/users' : '/dashboard';
  }

  /** Map a backend user (name + role) to the topbar CurrentUser shape. */
  private toCurrentUser(name: string, role: string): CurrentUser {
    return {
      name,
      initials: this.deriveInitials(name),
      role,
      entity: DEFAULT_ENTITY,
      entityShort: DEFAULT_ENTITY_SHORT,
    };
  }

  /** First letters of the first two words, e.g. "Jane Doe" -> "JD". */
  private deriveInitials(name: string): string {
    const parts = name.trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) {
      return 'N';
    }
    const initials = parts
      .slice(0, 2)
      .map((p) => p.charAt(0).toUpperCase())
      .join('');
    return initials || name.slice(0, 2).toUpperCase();
  }

  private setUser(user: CurrentUser): void {
    this.userSignal.set(user);
    if (typeof localStorage !== 'undefined') {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(user));
      } catch {
        // ignore storage failures (private mode, quota)
      }
    }
  }

  private clearUser(): void {
    this.userSignal.set(null);
    this.userIdSignal.set(null);
    this.emailSignal.set(null);
    this.roleNameSignal.set(null);
    this.permissionsSignal.set([]);
    this.mustChangePasswordSignal.set(false);
    if (typeof localStorage !== 'undefined') {
      try {
        localStorage.removeItem(STORAGE_KEY);
      } catch {
        // ignore storage failures
      }
    }
  }

  private loadSession(): void {
    if (typeof localStorage === 'undefined') {
      return;
    }
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const user = JSON.parse(stored) as CurrentUser;
        if (user && user.name && user.role) {
          this.userSignal.set(user);
        }
      }
    } catch {
      try {
        localStorage.removeItem(STORAGE_KEY);
      } catch {
        // ignore storage failures
      }
    }
  }
}
